import React, {
	RefObject,
	useCallback,
	useEffect,
	useLayoutEffect,
	useState,
} from "react";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import CountryFlag from "../shared/CountryFlag";

type PropsType = {
	show: boolean;
	onHide: () => void;
	anchorRef: RefObject<HTMLElement | null>;
};

type Coords = {
	top: number;
	left: number;
	width: number;
};

const MENU_WIDTH = 220;
const MENU_EST_HEIGHT = 176;
const VIEWPORT_PAD = 8;
const GAP = 8;

const languages = [
	{ code: "en", flag: "US", labelKey: "english" },
	{ code: "tr", flag: "TR", labelKey: "turkish" },
	{ code: "ar", flag: "SA", labelKey: "arabic" },
] as const;

export default function LanguageFrame({ show, onHide, anchorRef }: PropsType) {
	const router = useRouter();
	const { t } = useTranslation();
	const activeLocale = router.locale || "en";
	const [coords, setCoords] = useState<Coords | null>(null);

	const languageMenuHandler = (lang: string) => {
		onHide();

		if (lang === activeLocale) return;

		try {
			localStorage.setItem("lang", lang);
		} catch {
			// Ignore storage failures (private mode, etc.)
		}
		void router.push(router.pathname, router.asPath, { locale: lang });
	};

	const updatePosition = useCallback(() => {
		const anchor = anchorRef.current;
		if (!anchor) return;

		const rect = anchor.getBoundingClientRect();
		const width = Math.min(MENU_WIDTH, window.innerWidth - VIEWPORT_PAD * 2);
		const isRtl =
			(router.locale || "en") === "ar" ||
			document.documentElement.dir === "rtl";

		// Align dropdown end-edge with trigger end-edge (right in LTR, left in RTL).
		let left = isRtl ? rect.left : rect.right - width;
		left = Math.max(
			VIEWPORT_PAD,
			Math.min(left, window.innerWidth - width - VIEWPORT_PAD),
		);

		let top = rect.bottom + GAP;
		if (top + MENU_EST_HEIGHT > window.innerHeight - VIEWPORT_PAD) {
			top = Math.max(VIEWPORT_PAD, rect.top - MENU_EST_HEIGHT - GAP);
		}

		setCoords({ top, left, width });
	}, [anchorRef, router.locale]);

	useLayoutEffect(() => {
		if (!show) {
			setCoords(null);
			return;
		}
		updatePosition();
	}, [show, updatePosition]);

	useEffect(() => {
		if (!show) return;

		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") onHide();
		};

		window.addEventListener("keydown", onKeyDown);
		window.addEventListener("resize", updatePosition);
		window.addEventListener("scroll", updatePosition, true);

		return () => {
			window.removeEventListener("keydown", onKeyDown);
			window.removeEventListener("resize", updatePosition);
			window.removeEventListener("scroll", updatePosition, true);
		};
	}, [show, onHide, updatePosition]);

	if (!show || !coords) return null;

	return (
		<>
			{/* Transparent dismiss layer — keeps dropdown feel, not a modal. */}
			<div
				className="fixed inset-0 z-[1000]"
				onClick={onHide}
				aria-hidden
			/>
			<section
				role="menu"
				aria-label="Language"
				style={{
					top: coords.top,
					left: coords.left,
					width: coords.width,
				}}
				className="fixed z-[1001] flex flex-col gap-1 rounded-xl border border-[rgba(147,197,253,0.14)] bg-[#101A30] p-2 shadow-[0_18px_50px_rgba(4,8,24,0.55)]">
				{languages.map((lang) => {
					const selected = activeLocale === lang.code;
					return (
						<button
							key={lang.code}
							type="button"
							role="menuitem"
							onClick={() => languageMenuHandler(lang.code)}
							className={`flex h-11 w-full cursor-pointer items-center gap-3 rounded-lg px-3 text-start transition-colors ${
								selected
									? "bg-kalon-accent/25 text-kalon-text"
									: "text-kalon-muted hover:bg-kalon-accent/15 hover:text-kalon-text"
							}`}>
							<span className="flex h-6 w-6 shrink-0 items-center overflow-hidden rounded-full">
								<CountryFlag
									code={lang.flag}
									size="L"
									hasBorderRadius={true}
									className="border-none"
								/>
							</span>
							<span className="text-sm font-semibold">
								{t(lang.labelKey)}
							</span>
						</button>
					);
				})}
			</section>
		</>
	);
}
